// Configuration et utilitaires pour Google Maps
export const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Types pour Google Maps
declare global {
  interface Window {
    google: typeof google;
  }
}

// Vérifier si Google Maps est chargé
export const isGoogleMapsLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window.google && !!window.google.maps;
};

// Charger le script Google Maps
export const loadGoogleMapsScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGoogleMapsLoaded()) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Erreur lors du chargement de Google Maps'));
    
    document.head.appendChild(script);
  });
};

// Initialiser les services Google Maps
export const initializeGoogleMapsServices = async () => {
  if (!isGoogleMapsLoaded()) {
    await loadGoogleMapsScript();
  }
  
  return {
    autocompleteService: new google.maps.places.AutocompleteService(),
    geocoder: new google.maps.Geocoder(),
    placesService: new google.maps.places.PlacesService(document.createElement('div')),
  };
};

// Validation d'adresse
export interface AddressValidationResult {
  isValid: boolean;
  confidence: 'high' | 'medium' | 'low';
  formattedAddress: string;
  coordinates: { lat: number; lng: number } | null;
  components: {
    street_number?: string;
    route?: string;
    locality?: string;
    administrative_area_level_1?: string;
    postal_code?: string;
    country?: string;
  };
}

// Géocoder une adresse avec validation
export const validateAndGeocodeAddress = async (
  address: string
): Promise<AddressValidationResult> => {
  if (!isGoogleMapsLoaded()) {
    await loadGoogleMapsScript();
  }

  const geocoder = new google.maps.Geocoder();
  
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
        const result = results[0];
        const location = result.geometry?.location;
        
        if (location) {
          // Extraire les composants d'adresse
          const components: any = {};
          result.address_components?.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) components.street_number = component.long_name;
            if (types.includes('route')) components.route = component.long_name;
            if (types.includes('locality')) components.locality = component.long_name;
            if (types.includes('administrative_area_level_1')) components.administrative_area_level_1 = component.short_name;
            if (types.includes('postal_code')) components.postal_code = component.long_name;
            if (types.includes('country')) components.country = component.long_name;
          });

          // Déterminer la confiance
          const hasStreetAddress = components.street_number && components.route;
          const hasLocality = components.locality && components.administrative_area_level_1;
          const hasPostalCode = components.postal_code;
          
          let confidence: 'high' | 'medium' | 'low' = 'low';
          if (hasStreetAddress && hasLocality && hasPostalCode) {
            confidence = 'high';
          } else if (hasStreetAddress && hasLocality) {
            confidence = 'medium';
          }

          resolve({
            isValid: true,
            confidence,
            formattedAddress: result.formatted_address || address,
            coordinates: { lat: location.lat(), lng: location.lng() },
            components,
          });
        } else {
          resolve({
            isValid: false,
            confidence: 'low',
            formattedAddress: address,
            coordinates: null,
            components: {},
          });
        }
      } else {
        resolve({
          isValid: false,
          confidence: 'low',
          formattedAddress: address,
          coordinates: null,
          components: {},
        });
      }
    });
  });
};
